import { Button } from "@ancestors/components/Button";
import { useCallback, use } from "react";
import { isDevelopment } from "~/lib/utils";
import { ScriptContext } from "~/providers/script/scriptContext";

interface ErrorScreenProps {
  mode?: "detailed" | "minimal";
  error: unknown;
  resetError?: () => void;
}

export function ErrorScreen({
  error,
  mode = isDevelopment() ? "detailed" : "minimal",
  resetError,
}: ErrorScreenProps) {
  const context = use(ScriptContext);
  const script = context?.script;

  const errorType = attemptGetFromUnknown(error, "type") ?? null;
  const message = attemptGetFromUnknown(error, "message") ?? "Unknown error";

  const handleReload = useCallback(() => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  }, [resetError]);

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-4 text-center">
      <LucideCircleAlert />

      {script ? (
        <>
          {}
          <p
            className="text-lg font-bold"
            // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
            dangerouslySetInnerHTML={{ __html: script.html("error:description") }}
          />

          <Button onClick={handleReload}>{script.text("error:button")}</Button>
        </>
      ) : (
        <>
          <p className="text-lg font-bold">Oh no! Something went wrong 😢</p>
          <Button onClick={handleReload}>Refresh the page</Button>
        </>
      )}

      {mode === "detailed" ? (
        <pre className="whitespace-pre-wrap">
          {errorType && `[${errorType}]:`}
          {message.includes("\n") ? message : `\n${message}`}
        </pre>
      ) : (
        <p className="text-muted-foreground">
          {script
            ? script.text("error:note")
            : "If the error persists, please see us at the tech table."}
        </p>
      )}
    </div>
  );
}

function attemptGetFromUnknown(unknown: unknown, key: string) {
  return typeof unknown === "object" && unknown !== null && key in unknown
    ? (unknown as any)[key]
    : undefined;
}

function LucideCircleAlert(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="2em" height="2em" viewBox="0 0 24 24" {...props}>
      <g fill="none" stroke="#FF7F7F" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
        <circle cx="12" cy="12" r="10"></circle>
        <path d="M12 8v4m0 4h.01"></path>
      </g>
    </svg>
  );
}
